import { useState } from "react";
import { t } from "../../i18n";

export type AuthenticationConnection = {
  id: string;
  kind: "native" | "oidc";
  name: string;
  enabled: boolean;
  externallyManaged: boolean;
  issuer?: string | null;
  nativePolicy?: {
    registrationEnabled: boolean;
    passwordEnabled: boolean;
    passkeyEnabled: boolean;
    magicLinkEnabled: boolean;
    mfaRequired: boolean;
  } | null;
};

export function AuthenticationConnectionsEditor({
  connections,
  onNativePolicyChange,
  onCreate,
}: {
  connections: AuthenticationConnection[];
  onNativePolicyChange: (
    connection: AuthenticationConnection,
    policy: NonNullable<AuthenticationConnection["nativePolicy"]>,
  ) => void;
  onCreate: (input: Record<string, unknown>) => Promise<void>;
}) {
  const [kind, setKind] = useState<"native" | "oidc">("native");
  const [name, setName] = useState("");
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const create = async () => {
    await onCreate(
      kind === "native"
        ? {
            kind,
            name,
            emailDomains: [],
            nativePolicy: {
              registrationEnabled: false,
              passwordEnabled: true,
              passkeyEnabled: false,
              magicLinkEnabled: true,
              mfaRequired: false,
            },
          }
        : {
            kind,
            name,
            issuer,
            clientId,
            clientSecret,
            scopes: "openid profile email",
            emailDomains: [],
            claimMapping: {},
            groupRoleMapping: {},
            jitProvisioning: false,
            inviteOnly: true,
          },
    );
    setName("");
    setClientSecret("");
  };
  return (
    <section className="panel p-5">
      <h2 className="font-bold">{t("Organization authentication")}</h2>
      <p className="mt-1 text-sm text-stone-500">
        {t(
          "Configure how users sign in to this organization. Environment-managed OIDC connections are read-only.",
        )}
      </p>
      <div className="mt-4 space-y-3">
        {connections.map((connection) => (
          <div
            key={connection.id}
            className="rounded-xl border border-stone-200 p-4 dark:border-white/10 dark:bg-white/3"
          >
            <div className="flex items-center justify-between">
              <strong>{connection.name}</strong>
              <span className="text-xs text-stone-500 uppercase">
                {connection.kind}
                {connection.externallyManaged ? ` · ${t("read-only")}` : ""}
              </span>
            </div>
            {connection.issuer && (
              <p className="mt-1 text-xs text-stone-500">{connection.issuer}</p>
            )}
            {connection.kind === "native" && connection.nativePolicy && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {(
                  [
                    ["registrationEnabled", "Allow registration"],
                    ["passwordEnabled", "Allow passwords"],
                    ["passkeyEnabled", "Allow passkeys"],
                    ["magicLinkEnabled", "Allow magic links"],
                    ["mfaRequired", "Require MFA"],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={connection.nativePolicy?.[key] ?? false}
                      disabled={connection.externallyManaged}
                      onChange={(event) =>
                        onNativePolicyChange(connection, {
                          ...connection.nativePolicy!,
                          [key]: event.target.checked,
                        })
                      }
                    />
                    {t(label)}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
        {!connections.length && (
          <p className="text-sm text-stone-500">
            {t("No authentication connection is configured.")}
          </p>
        )}
      </div>
      <div className="mt-5 border-t border-stone-200 pt-4 dark:border-white/10">
        <h3 className="font-semibold">{t("Add authentication connection")}</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <select
            className="field"
            value={kind}
            onChange={(event) =>
              setKind(event.target.value as "native" | "oidc")
            }
          >
            <option value="native">{t("Native authentication")}</option>
            <option value="oidc">{t("OIDC authentication")}</option>
          </select>
          <input
            className="field"
            placeholder={t("Connection name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {kind === "oidc" && (
            <>
              <input
                className="field"
                type="url"
                placeholder={t("Issuer URL")}
                value={issuer}
                onChange={(event) => setIssuer(event.target.value)}
              />
              <input
                className="field"
                placeholder={t("Client ID")}
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              />
              <input
                className="field"
                type="password"
                placeholder={t("Client secret")}
                value={clientSecret}
                onChange={(event) => setClientSecret(event.target.value)}
              />
            </>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            className="button-primary"
            disabled={
              !name ||
              (kind === "oidc" && (!issuer || !clientId || !clientSecret))
            }
            onClick={() => void create()}
          >
            {t("Add connection")}
          </button>
        </div>
      </div>
    </section>
  );
}
